import { View } from "react-native-web"
import { useDatastore, DEPRECATED_useModuleUserGlobalData } from "util/datastore"
import { Heading, UtilityText } from "component/text"
import { HorizBox, Pad, PadBox } from "component/basics"
import { colorSurfaceInfo, colorSurfaceWarning, colorTextHint, colorTextSecondaryOnLightColorBg, colorTextWarning } from "component/color"
import { ToggleWithLabelWidget } from "open-modules/zdf/component/form"
import { useConfig } from "util/features"
import { Feature } from "util/stdtypes"
import { AccountConfig } from "structure/account"
import { setPushNotificationPermission, usePushNotificationPermission } from "open-modules/zdf/component/notifications/pushnotification"
import { logEventAsync } from "util/eventlog"
import { getBrowserNotificationPermission, isIframe, requestBrowserPushNotificationHandler } from "platform-specific/zdf/pushNotifs"
import { RichText } from "component/richtext"
import { ClickableBanner } from "component/banner"
import { Warning, WarningAlt } from "@carbon/icons-react"
import { arePushNotificationsSupported } from "platform-specific/zdf/pushNotifs"
import { useState } from "react"
export type NotificationConfig = {
    pushNotificationToggleEnabled?: boolean,
    emailNotificationToggleEnabled?: boolean,
}

export const AccountNotificationPermissionFeature: Feature<AccountConfig, {}> = {
    name: "Notification Permission",
    key: "notificationpermission",
    config: {
        accountWidgets: [NotificationPermissionWidget]
    },
    defaultConfig: {
        pushNotificationToggleEnabled: false,
        emailNotificationToggleEnabled: false,
    }
}


export function NotificationPermissionWidget() {
    const config = useConfig<NotificationConfig>();

    return (
        <View>
            <Pad size={40} />
            <Heading type="small" label="Notifications" />
            <Pad size={24} />
            {config.pushNotificationToggleEnabled && <PushNotificationToggle />}
            {config.emailNotificationToggleEnabled && <EmailNotificationToggle />}
        </View>
    );
}

export function PushNotificationToggle() {
    const hasPushNotificationPermission = usePushNotificationPermission();
    const datastore = useDatastore();

    async function updatePushPermissionGlobal(permission: boolean) {
        setPushNotificationPermission({ datastore, value: permission });
        logEventAsync(datastore, "push-notifs-update-permission", { permission, func: "updatePushPermissionGlobalAsync" });
    }

    return (
        <>
            <ToggleWithLabelWidget
                labelWidget={PushNotificationLabelWidget}
                value={hasPushNotificationPermission}
                onChange={updatePushPermissionGlobal}
                spread
                ariaText={"Push notifications"}
                testID={"Push notifications"}
            />
            <NoBrowserPushNotificationWarningComponent />
        </>
    );
}

export function PushNotificationLabelWidget() {
    return (
        <View>
            <UtilityText label="Push notifications" type="large" />
            <Pad size={4} />
            <UtilityText label="For replies" type="small" color={colorTextHint} />
        </View>
    );
}

export function EmailNotificationToggle() {
    const datastore = useDatastore();
    // Email permissions use an object with the property "status" instead of a simple boolean to handle opt-out behavior
    // useModuleUserGlobalData returns null for both non-existent values and false values
    // With a simple boolean, the toggle would either be permanently stuck on true or default to false
    const emailPermission = DEPRECATED_useModuleUserGlobalData("notifications", ["emailPermission"]) || { status: true };
    
    async function updateEmailPermissionAsync(permission: boolean) {
        logEventAsync(datastore, "email-notifs-update-permission", { permission, func: "updateEmailPermissionAsync" });
        await datastore.DEPRECATED_setModuleUserGlobal("notifications", ["emailPermission"], { status: permission });
    }

    return (
        <ToggleWithLabelWidget
            labelWidget={EmailNotificationLabelWidget}
            value={emailPermission.status}
            onChange={updateEmailPermissionAsync}
            spread
            ariaText={"Email notifications"}
            testID={"Email notifications"}
        />
    );
}

export function EmailNotificationLabelWidget() {
    return (
        <View>
            <UtilityText label="Email notifications" type="large" />
            <Pad size={4} />
            <UtilityText label="For replies and moderation decisions" type="small" color={colorTextHint} />
        </View>
    );
}

function NoBrowserPushNotificationWarningComponent() {
    // Edge case: In case of an Iframe browser permission cannot be default
    let browserPermissionInitial = getBrowserNotificationPermission();
    if(browserPermissionInitial === "denied" && isIframe()) browserPermissionInitial = "default";

    const [browserPermission, setBrowserPermission] = useState(browserPermissionInitial);
    const pushPermission = DEPRECATED_useModuleUserGlobalData("notifications", ["pushPermission"]) ?? false;
    const pushNotificationsSupported = arePushNotificationsSupported()
    const datastore = useDatastore();

    const updatePermissionState = () => {
        setBrowserPermission(getBrowserNotificationPermission());
    }
    const { requestPermission} = requestBrowserPushNotificationHandler({
        datastore,
        onCancel: updatePermissionState,
        onError: updatePermissionState,
        onFinish: (permission) => {setBrowserPermission(permission ?? "default")},

    })

    const showWarning = pushNotificationsSupported && pushPermission && browserPermission !== "granted";
    const warningText =
        browserPermission === "denied"
            ? "Your browser has blocked push notifications on this page.\nYou must also unblock them in the browser settings."
            : "Your browser has has not accepted push notifications on this page yet. \n**Click here to grant them**";

    const clickHandler = browserPermission === "default" ?
        requestPermission : null

    return (
        <>
            {showWarning && (
                <PadBox vert={4}>
                    <ClickableBanner color={colorSurfaceWarning} testID={"browserPushNotificationWarning"} onPress={clickHandler}>
                        <HorizBox center>
                            {browserPermission==="default" ? <Warning/> :<WarningAlt />}
                            <Pad />
                            <RichText label={warningText} color={colorTextSecondaryOnLightColorBg} />
                        </HorizBox>
                    </ClickableBanner>
                </PadBox>
            )}
        </>
    );
}